import React, { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { PDLAnalysisCard } from './PDLAnalysisCard';
import { PdlPagination } from './PdlPagination';

/**
 * Étape 5 TURPE — extrait de CreateAnalyseTURPE.jsx pour réduire la taille
 * du composant parent. Reçoit toutes les dépendances via un objet `ctx` unique.
 *
 * Optimisations perfs (multi-PDL massif) :
 *  - <PDLAnalysisCard /> est mémoïsé : pas de re-render en cascade.
 *  - Au-delà de 10 PDL on n'affiche QUE les 10 premiers par défaut (~90 charts SVG
 *    au lieu de 540 pour 60 PDL → DOM léger, scroll fluide).
 *  - Champ de recherche par numéro PDL pour cibler un PDL précis sans tout afficher.
 *  - Bouton "Afficher tous" pour les utilisateurs qui veulent la vue exhaustive.
 */
const SEUIL_PAGINATION = 10;

export const Step5Analyse = ({ ctx }) => {
  const [searchPdl, setSearchPdl] = useState('');
  const [page, setPage] = useState(0);
  const {
    CommentaireInput,
    PuissanceInput,
    agregerParHeure,
    calculerConsommationsParPlage,
    calculsAJour,
    calculsRapport,
    commentairesParPDL,
    consommationsParPlageRef,
    formData,
    handleSaveCommentaire,
    handleSaveConsommationParPlage,
    handleSavePuissanceCourbe,
    handleSavePuissanceParPlage,
    metadonneesPDL,
    plagesHorairesCustomRef,
    preconisationsPuissanceParPlageRef,
    preconisationsPuissanceRef,
    puissancesMaxParPDL,
    puissancesPreconiseesValidees,
    sauvegarderBrouillon,
    setPlagesHorairesModalPDL,
    setStep,
    setVisibiliteCourbes,
    setZoomReset,
    visibiliteCourbes,
    zoomReset,
  } = ctx;

  return (
    <div className="bg-white border border-[#BFC6CC] rounded-lg p-8">
      <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-2">
        Analyse et Préconisation TURPE
      </h2>
      <p className="text-[#687175] mb-6">
        Analysez les données de chaque PDL et saisissez vos préconisations de puissance par tranche horosaisonnière.
      </p>

      {Object.keys(puissancesMaxParPDL).length > 0 ? (
        <div className="space-y-8">
          {(() => {
            const allPdls = Object.entries(puissancesMaxParPDL);
            const total = allPdls.length;
            // Filtrage par recherche (numéro PDL)
            const filtres = searchPdl.trim()
              ? allPdls.filter(([pdl]) => pdl.toLowerCase().includes(searchPdl.trim().toLowerCase()))
              : allPdls;
            // Pagination par PAGES : seules 10 cartes sont montées à la fois (mémoire maîtrisée).
            const totalPages = Math.max(1, Math.ceil(filtres.length / SEUIL_PAGINATION));
            const safePage = Math.min(page, totalPages - 1);
            const limite = filtres.slice(safePage * SEUIL_PAGINATION, (safePage + 1) * SEUIL_PAGINATION);
            return (
              <>
                {total > SEUIL_PAGINATION && (
                  <div className="flex flex-col gap-3 p-3 bg-[#F0F7FF] border border-[#BFD9FF] rounded-lg" data-testid="step5-perfs-bar">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="relative flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#687175]" />
                        <input
                          type="text"
                          value={searchPdl}
                          onChange={(e) => { setSearchPdl(e.target.value); setPage(0); }}
                          placeholder={`Rechercher un PDL parmi ${total}…`}
                          className="w-full pl-9 pr-3 py-2 text-sm border border-[#BFC6CC] rounded bg-white"
                          data-testid="step5-search-pdl"
                        />
                      </div>
                      <span className="text-xs text-[#687175]">
                        {filtres.length} PDL{filtres.length > 1 ? 's' : ''}{filtres.length !== total ? ` (sur ${total})` : ''}
                      </span>
                    </div>
                    <PdlPagination page={safePage} totalPages={totalPages} total={filtres.length} pageSize={SEUIL_PAGINATION} onPage={setPage} testidPrefix="step5" />
                  </div>
                )}
                {limite.map(([pdl, resultatPDL]) => {
            const pdlConfig = formData.pdls.find(p => p.pdl === pdl);
            const formuleActuelle = pdlConfig?.formule_actuelle || '';

            // Récupère les valeurs depuis les refs (pas de re-render quand elles changent)
            const precoParPlage = preconisationsPuissanceParPlageRef.current[pdl] || {};
            const precoUniqueCourbe = preconisationsPuissanceRef.current[pdl] || 0;
            const precoUniqueCourbeValidee = puissancesPreconiseesValidees[pdl] || 0;

            // Calcule (ou récupère du cache) les consommations par plage
            const tousLesPoints = resultatPDL.tousLesPoints || [];
            let consoActuelles = consommationsParPlageRef.current[pdl];
            if (!consoActuelles && tousLesPoints.length > 0) {
              const formuleCalcul = pdlConfig?.formule_actuelle || 'HTACU5 PF';
              const consoCalculees = calculerConsommationsParPlage(tousLesPoints, formuleCalcul, pdl);
              consommationsParPlageRef.current = {
                ...consommationsParPlageRef.current,
                [pdl]: consoCalculees,
              };
              consoActuelles = consoCalculees;
            } else if (!consoActuelles) {
              consoActuelles = { Pointe: 0, HPH: 0, HCH: 0, HPB: 0, HCB: 0 };
            }

            return (
              <PDLAnalysisCard
                key={pdl}
                pdl={pdl}
                resultatPDL={resultatPDL}
                pdlMetadonnees={metadonneesPDL[pdl] || {}}
                pdlConfig={pdlConfig}
                precoParPlage={precoParPlage}
                precoUniqueCourbe={precoUniqueCourbe}
                precoUniqueCourbeValidee={precoUniqueCourbeValidee}
                consoActuelleParPlage={consoActuelles}
                formuleActuelle={formuleActuelle}
                visibiliteCourbes={visibiliteCourbes}
                setVisibiliteCourbes={setVisibiliteCourbes}
                zoomReset={zoomReset}
                setZoomReset={setZoomReset}
                calculsAJour={calculsAJour}
                calculsRapport={calculsRapport}
                setPlagesHorairesModalPDL={setPlagesHorairesModalPDL}
                hasCustomPlages={!!plagesHorairesCustomRef.current[pdl]}
                handleSavePuissanceParPlage={handleSavePuissanceParPlage}
                handleSavePuissanceCourbe={handleSavePuissanceCourbe}
                handleSaveConsommationParPlage={handleSaveConsommationParPlage}
                handleSaveCommentaire={handleSaveCommentaire}
                initialCommentaire={commentairesParPDL[pdl]}
                PuissanceInput={PuissanceInput}
                CommentaireInput={CommentaireInput}
                agregerParHeure={agregerParHeure}
                calculerConsommationsParPlage={calculerConsommationsParPlage}
              />
            );
          })}
                {total > SEUIL_PAGINATION && filtres.length > 0 && (
                  <PdlPagination page={safePage} totalPages={totalPages} total={filtres.length} pageSize={SEUIL_PAGINATION} onPage={setPage} testidPrefix="step5-bottom" />
                )}
                {filtres.length === 0 && (
                  <div className="text-center py-8 text-sm text-[#687175]">
                    Aucun PDL ne correspond à « {searchPdl} ».
                  </div>
                )}
              </>
            );
          })()}
        </div>
      ) : (
        <div className="p-12 bg-[#F5F6F9] rounded-lg text-center">
          <p className="text-[#687175]">
            Aucune donnée importée. Retournez à l'étape 4 pour importer des données brutes.
          </p>
        </div>
      )}

      <div className="flex justify-between gap-3 pt-6">
        <button onClick={() => setStep(4)} className="px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg">
          ← Retour
        </button>
        <div className="flex gap-3">
          <button
            onClick={sauvegarderBrouillon}
            className="flex items-center gap-2 px-4 py-2 border border-[#F9C80E] text-[#F9C80E] hover:bg-[#F9C80E] hover:text-white rounded-lg"
          >
            💾 Enregistrer Brouillon
          </button>
          <button
            onClick={() => setStep(6)}
            disabled={Object.keys(puissancesMaxParPDL).length === 0}
            className="bg-[#397DFF] hover:bg-[#4672B6] text-white py-3 px-8 rounded-lg disabled:opacity-50"
          >
            Suivant : Validation →
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step5Analyse;
